<template>
  <PmResourcePure
    v-if="resource"
    v-bind="normalizedResource"
    @create-resource-allocation="createResourceAllocation"
    @search="setActionbarFilter"
    @select="onSelect"
    @unselect="onUnselect"
    @show-details="showDetails"
  >
    <template #default="slotProps">
      <!-- <div v-show="props.isInViewport"> -->
      <template v-if="!resourceAllocations">
        <PmResourceAllocationPure
          v-for="index in numberOfResourceAllocations"
          :id="`skeleton.${index}`"
          :key="index"
          type="skeleton"
          :class="slotProps.classes"
          :is-skeleton="true"
        />
      </template>

      <template v-for="resourceAllocation in resourceAllocations">
        <PmResourceAllocation
          v-if="resourceAllocation && type"
          :id="resourceAllocation.id"
          :key="resourceAllocation.id"
          :class="slotProps.classes"
          :type="type"
          :edit-button-visible="editButtonsVisible"
          :container-start-date="containerStartDate"
          :parent-start-date="parentDates.start"
          :parent-end-date="parentDates.end"
          :force-show-time="hasDeviatingDates"
        />
      </template>
      <!-- </div> -->

      <div
        v-if="xstate.state.value.matches('createResourceAllocation')"
        class="PmResource-createSkeleton"
      >
        <div ref="popoverElement" class="PmResource-popoverElement"></div>

        <PmResourceAllocationPure
          id="createSkeleton"
          type="skeleton"
          :class="slotProps.classes"
          :is-skeleton="true"
          title="Blupp"
        />

        <PmResourceAllocationEdit
          v-if="type"
          :resource-id="id"
          :address-id="xstate.state.value.context.addressId"
          :vehicle-id="xstate.state.value.context.vehicleId"
          :type="type"
          :popover-element="popoverElement"
          @close="xstate.service.value.send('FINISH')"
          @create-resource-request="
            (payload) => {
              createResourceRequestId = payload.resourceAllocationId
              isCreateResourceRequestVisible = true
            }
          "
        />
      </div>

      <PmMultipleResourceAllocationsRequest
        v-if="isCreateResourceRequestVisible && createResourceRequestId"
        :resource-allocation-ids="[createResourceRequestId]"
        @close="
          () => {
            createResourceRequestId = undefined
            isCreateResourceRequestVisible = false
          }
        "
      />
    </template>
  </PmResourcePure>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { IS_DEVELOPMENT } from '@/constants/general'

const COMPONENT_NAME = 'PmResource'

export const propTypes = {} as const

export default defineComponent({
  name: COMPONENT_NAME,
})
</script>

<script setup lang="ts">
import { ref, computed, toRef, watch } from 'vue'
import { find, orderBy, isNil } from 'lodash-es'
import { useStore } from 'vuex'
import * as Sentry from '@sentry/vue'

import {
  RESOURCE_TYPE_LOOKUP,
  RESOURCE_TYPE,
  DATA_MODAL_TYPE,
  STATUS_RESOURCE_ALLOCATION_LOOKUP,
  STATUS_RESOURCE_ALLOCATION_SORT_ORDER_CALENDAR,
} from '@/constants/persoplan'
import { ICONS } from '@/constants/icons'
import { EVENT } from '@/constants/events'
import {
  isValidDate,
  parseServerDateString,
  isSameDates,
} from '@/utilities/date'
import useLazyloadedResourceDetails from '@/composition/useLazyloadedResourceDetails'
import EventHub from '@/eventHub'
import { persoplanStateKey, injectStrict } from '@/utilities/inject'
import { lookup } from '@/utilities/misc'
import { useCachedQuery } from '@/composition/useCachedQuery'
import { useXState } from '@/composition/useXState'
import { PmResourceState } from '@/components/persoplan/PmResource/PmResourceState'
import { useSelectionResourceAllocations } from '@/pinia/selectionResourceAllocations'
import PmResourcePure, {
  type Props as PropsResourcePure,
  type Emits as EmitsResourcePure,
} from '@/components/persoplan/PmResource/PmResourcePure.vue'
import PmResourceAllocation from '@/components/persoplan/PmResourceAllocation/PmResourceAllocation.vue'
import PmResourceAllocationPure from '@/components/persoplan/PmResourceAllocation/PmResourceAllocationPure.vue'
import PmResourceAllocationEdit from '@/components/persoplan/PmResourceAllocation/PmResourceAllocationEdit.vue'
import {
  ResourceDetailsDocument,
  ResourceDocument,
} from '../../../../generated/graphql'
import { useSelectionResourcesItem } from '@/composition/useSelectionResources'
import PmMultipleResourceAllocationsRequest from '@/components/persoplan/PmMultipleResourceAllocationsRequest/PmMultipleResourceAllocationsRequest.vue'

export interface Props {
  id: number
  editButtonsVisible?: boolean
  containerStartDate: Date
  isInViewport?: boolean
  /** Only used during development for debugging */
  isLazyloadedDetailsVisible?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isInViewport: true,
})

const DEBUG_LAZYLOADED_DETAILS = true

const store = useStore()
const persoplanState = injectStrict(persoplanStateKey)
const popoverElement = ref()

const xstate = useXState(PmResourceState)
const selectionResourceAllocations = useSelectionResourceAllocations()

/**
 * Start resource request after creating an new allocation
 */
const isCreateResourceRequestVisible = ref(false)
const createResourceRequestId = ref<number>()

useLazyloadedResourceDetails({
  id: toRef(props, 'id'),
  isInViewport: toRef(props, 'isInViewport'),
})

const resourceQuery = useCachedQuery(ResourceDocument, () => {
  const queryVariables = store.getters['queryVariables/calendar']

  return {
    ...queryVariables,
    id: props.id,
  }
})

const resource = computed(() => resourceQuery.result?.value?.resource)

const resourceDetailsQuery = useCachedQuery(ResourceDetailsDocument, () => {
  const queryVariables = store.getters['queryVariables/calendar']

  return {
    ...queryVariables,
    ids: [props.id],
  }
})

const resourceDetails = computed(
  () => resourceDetailsQuery.result?.value?.lazyloadedResourceDetails?.[0]
)

const type = computed(() => {
  const resourceTypeId = resource.value?.resourceFunction?.resourceType?.id
  if (!resourceTypeId) throw new Error('resourceTypeId is undefined')

  const result = lookup(resourceTypeId, RESOURCE_TYPE_LOOKUP)
  if (!result) throw new Error('resourceTypeId is undefined')
  return result
})

const selectionResourcesItem = useSelectionResourcesItem({
  id: props.id,
  type: type,
})

const resourceAllocations = computed(() => {
  // Comment in for lazyloaded resource allocations
  // const resourceAllocations = resourceDetails.value?.resourceAllocations

  const resourceAllocations = resource.value?.resourceAllocations

  if (
    IS_DEVELOPMENT &&
    DEBUG_LAZYLOADED_DETAILS &&
    props.isLazyloadedDetailsVisible !== true
  ) {
    return
  }

  if (!resourceAllocations) return

  // Sort by status and and name
  const result = orderBy(
    resourceAllocations,
    [
      // Status
      (resourceAllocation) => {
        const statusId = resourceAllocation?.resourceAllocationState?.id
        if (!statusId) return -1

        const status = lookup(statusId, STATUS_RESOURCE_ALLOCATION_LOOKUP)
        if (!status) {
          console.log('no status lookup result')

          return -1
        }

        const index =
          STATUS_RESOURCE_ALLOCATION_SORT_ORDER_CALENDAR.indexOf(status)
        return index
      },

      // Last name
      (resourceAllocation) => {
        return resourceAllocation?.address?.surname
      },

      // First name
      (resourceAllocation) => {
        return resourceAllocation?.address?.firstName
      },

      // Id to make it stable in case previous sort order is not unique
      'id',
    ],
    ['asc', 'asc', 'asc', 'asc']
  )

  return result
})

function createResourceAllocation(
  payload: EmitsResourcePure['createResourceAllocation'][0]
) {
  if (
    type.value === RESOURCE_TYPE.ADDRESS &&
    (payload.resourceType === 'address' ||
      payload.resourceType === 'freelancer')
  ) {
    return xstate.service.value.send('CREATE_RESOURCE_ALLOCATION_ADDRESS', {
      addressId: payload.id,
    })
  }

  if (
    type.value === RESOURCE_TYPE.VEHICLE &&
    payload.resourceType === 'vehicle'
  ) {
    return xstate.service.value.send('CREATE_RESOURCE_ALLOCATION_VEHICLE', {
      vehicleId: payload.id,
    })
  }

  throw new Error('createResourceAllocation: No type defined')
}

function onSelect() {
  selectionResourcesItem.add()
}

function onUnselect() {
  selectionResourcesItem.remove()
}

function setActionbarFilter() {
  const settings = {
    type: type.value,
    resourceFunctionId: resource.value?.resourceFunction?.id,
  }

  EventHub.$emit(EVENT.ACTIONBAR_SET_FILTER, settings)
}

function showDetails() {
  EventHub.$emit(EVENT.DATA_MODAL_SHOW, {
    type: DATA_MODAL_TYPE.RESOURCE,
    id: props.id,
  })
}

/**
 * Aggregate the number of displayed resource allocations for displaying skeletons
 * and providing the proper height for the container until the actual data is lazyloaded
 */
const numberOfConfirmedResourceAllocations = computed(() => {
  const entry = find(resource.value?.allocationStatistics, {
    key: 'Bestätigt',
  })
  return entry?.value ? parseInt(entry.value) : 0
})

const numberOfRequestedResourceAllocations = computed(() => {
  const entry = find(resource.value?.allocationStatistics, {
    key: 'Anfrage',
  })
  return entry?.value ? parseInt(entry.value) : 0
})

const numberOfConsideredResourceAllocations = computed(() => {
  const entry = find(resource.value?.allocationStatistics, {
    key: 'Angedacht',
  })
  return entry?.value ? parseInt(entry.value) : 0
})

const numberOfReservedResourceAllocations = computed(() => {
  const entry = find(resource.value?.allocationStatistics, {
    key: 'Reserviert',
  })
  return entry?.value ? parseInt(entry.value) : 0
})

const numberOfCancelledResourceAllocations = computed(() => {
  const entry = find(resource.value?.allocationStatistics, {
    key: 'Abgesagt',
  })
  return entry?.value ? parseInt(entry.value) : 0
})

const numberOfResourceAllocations = computed(() => {
  /**
   *  @todo: Include resources only when active in view settings
   */
  return (
    numberOfConfirmedResourceAllocations.value +
    numberOfRequestedResourceAllocations.value +
    numberOfConsideredResourceAllocations.value +
    numberOfReservedResourceAllocations.value
  )
})

/**
 * For debugging purposes throw an error, when the actual data and the
 * aggregated number of resource allocations is not equal
 */
watch([numberOfResourceAllocations, resourceAllocations], () => {
  if (isNil(resourceAllocations.value)) return

  if (numberOfResourceAllocations.value === resourceAllocations.value.length) {
    return
  }

  const error = new Error(
    'Counted resource allocations from api and data is not equal'
  )
  Sentry.captureException(error, {
    extra: {
      resourceId: props.id,
      numberCalculatedInApi: numberOfResourceAllocations.value,
      actualNumber: resourceAllocations.value.length,
    },
  })

  // throw error
})

const normalizedResource = computed<PropsResourcePure>(() => {
  const hasResourceAllocations = numberOfReservedResourceAllocations.value > 0

  const result: PropsResourcePure = {
    startDate: startDate.value,
    endDate: endDate.value,
    shortText: shortText.value ?? undefined,
    quantityNeeded: resource.value?.quantity ?? undefined,
    quantityAchieved: numberOfConfirmedResourceAllocations.value,
    quantityUnclear: quantityUnclear.value,
    icon: icon.value,
    type: type.value,
    selectVisible: selectVisible.value,
    canBeSelected: canBeSelected.value,
    hasSearchButton: hasSearchButton.value,
    isSelected: isSelected.value,
    isLocked: resource.value?.timesScheduled ?? false,
    isTimeVisible: hasDeviatingDates.value && !hasResourceAllocations,
    note: resource.value?.custom1,
    numberOfResourceAllocations: numberOfResourceAllocations.value,
  }

  return result
})

const startDate = computed(() => {
  const date = parseServerDateString(resource.value?.startDate)
  if (!isValidDate(date)) throw new Error('date is not a valid date')
  return date
})

const endDate = computed(() => {
  const date = parseServerDateString(resource.value?.endDate)
  if (!isValidDate(date)) throw new Error('date is not a valid date')
  return date
})

const appointmentStartDate = computed(() =>
  parseServerDateString(resource.value?.jobAppointment?.startDate)
)

const appointmentEndDate = computed(() =>
  parseServerDateString(resource.value?.jobAppointment?.endDate)
)

/**
 * Do the dates deviate from the appointment?
 */
const hasDeviatingDates = computed(() => {
  return isSameDates(
    { start: startDate.value, end: endDate.value },
    { start: appointmentStartDate.value, end: appointmentEndDate.value }
  )
})

/**
 * The dates of the appointment or the ressource when they deviate
 */
const parentDates = computed(() => {
  const hasValidAppointmentDates =
    isValidDate(appointmentStartDate.value) &&
    isValidDate(appointmentEndDate.value)

  if (hasDeviatingDates.value || !hasValidAppointmentDates) {
    // Use the dates of the ressource
    return {
      start: startDate.value,
      end: endDate.value,
    }
  }

  // Use the dates of the appointment
  return {
    start: appointmentStartDate.value,
    end: appointmentEndDate.value,
  }
})

const shortText = computed(() => {
  const abbreviation = resource.value?.resourceFunction?.abbreviation
  const label = resource.value?.resourceFunction?.caption

  if (abbreviation) return abbreviation
  if (label) return label
  return 'n/a'
})

const quantityUnclear = computed(() => {
  return (
    numberOfRequestedResourceAllocations.value +
    numberOfConsideredResourceAllocations.value
  )
})

const icon = computed(() => {
  if (type.value === RESOURCE_TYPE.VEHICLE) return ICONS.VEHICLE
  return undefined
})

const selectVisible = computed(() => {
  if (selectionResourceAllocations.isInSelectionMode) return false
  if (persoplanState.state.value.matches('edit')) return true

  return false
})

const canBeSelected = computed(() => {
  if (selectionResourcesItem.selectedType.value === undefined) return true
  return selectionResourcesItem.selectedType.value === type.value
})

const isSelected = computed(() => {
  return selectionResourcesItem.isSelected.value
})

const hasSearchButton = computed(() => {
  if (persoplanState.state.value.matches('edit')) return true

  return false
})

const createSkeletonVisibleNormalized = computed(() => {
  if (store.state.persoplan.resourceShowSkeletonIds.includes(props.id)) {
    return true
  }

  return false
})
</script>

<style lang="scss">
.PmResource {
  $block: &;

  &-createSkeleton {
    position: relative;
  }

  &-popoverElement {
    width: 10px;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
}
</style>
